<script lang="ts">
  /**
   * Användare-vy (konsoliderad — pilot-feedback 2026-05-05).
   *
   * Tidigare visade Lyktan två separata layouter under en segment-tab:
   * "Administratörer" (S-04-15) och "Användare/Pilot" (S-04-08). Jonas
   * upplevde tab-växlingen som extremt störande — fyra olika tabell-
   * layouter i samma vy. Vi konsoliderar nu till EN tabell med en
   * Typ-kolumn (Admin / Pilot), så att alla användare visas samtidigt
   * och admin slipper navigera mellan undermeny-vyer.
   *
   * URL-param `?typ=admin|pilot` har tagits bort. Befintliga bookmarks
   * lever vidare som no-op (queryparametern ignoreras).
   *
   * ============================================================
   * Datakällor
   * ============================================================
   *  - GET /api/admin/users          (adminUsersClient.listAdmins)
   *  - GET /api/admin/pilot-accounts (pilotAccountsClient.listPilotAccounts)
   *
   * Båda listorna laddas parallellt vid mount och vid refresh-knapp.
   * Eventuellt 401/403 i någon av dem räknas som unauthorized för hela
   * vyn (App-shellen tar över).
   *
   * ============================================================
   * Modal-disciplin (hard-rules bevaras 1:1 från sub-vyerna)
   * ============================================================
   *  - Lägg till administratör: tempo-lösenord visas EN gång, auto-clear efter
   *    90 sekunder, Escape/backdrop blockad medan lösenordet visas.
   *    Inget värde lagras i sessionStorage/localStorage.
   *  - Inaktivera admin: kräver MFA-step-up (TOTP-kod), self-deactivate
   *    förbjudet, MFA-gate-banner visas vid mfa_not_configured.
   *  - Skapa pilotkonto: engångskod visas EN gång, ingen backdrop-stäng,
   *    kod rensas vid stäng. Aldrig persisteras.
   *  - Inaktivera pilotkonto: enkel bekräftelse (ingen MFA — backend
   *    enforce:ar admin-roll + CSRF). Soft-delete + audit-trail på
   *    backend.
   *
   * ============================================================
   * Hard-rules (CLAUDE.md)
   * ============================================================
   *  - Inga fritextfält. `displayName`/`username` är kategoriska
   *    identifierare (max 100 tecken).
   *  - Inga råa databas-ID:n — UUID/opaque IDs överallt.
   *  - Cookie-auth via `credentials: 'include'`; CSRF-header på
   *    muterande anrop (lever i client-funktionerna).
   *  - Tempo-lösenord/engångskoder lagras ALDRIG i sessionStorage
   *    eller localStorage — bara $state som rensas vid modal-stäng.
   *  - Admin-walk-vy = blocker. Vi visar bara konto-info, aldrig
   *    promenad-data.
   *  - Engelska kod-identifierare, svenska användartexter.
   */
  import { onDestroy, onMount } from 'svelte';
  import {
    deactivateAdmin,
    inviteAdmin,
    listAdmins,
    type AdminUser,
  } from '../admin/adminUsersClient';
  import {
    createPilotAccount,
    deactivatePilotAccount,
    listPilotAccounts,
    type CreatePilotAccountSuccess,
    type PilotAccount,
  } from '../pilots/pilotAccountsClient';
  import {
    getUserContacts,
    updateUserContacts,
    validateUserContacts,
  } from './userContactsClient';
  import {
    buildPilotActivationDeepLink,
    buildPilotActivationSmsText,
  } from '../pilots/pilotActivationDeepLink';
  import { formatDate } from '../util/format';
  import QRCode from 'qrcode';

  type Props = {
    /** Aktuell inloggad admins user-id — används för att dölja egen-deaktivera-knapp. */
    currentUserId: string;
    /** Anropas vid 401/403 för att tvinga logout-flow i App.svelte. */
    onUnauthorized?: () => void;
    /**
     * Anropas när admin behöver aktivera tvåfaktor — App-shellen tar
     * användaren till Konto-tabben och öppnar MfaSetupView.
     */
    onMfaGateOpen?: () => void;
  };
  const { currentUserId, onUnauthorized, onMfaGateOpen }: Props = $props();

  // ===================================================================
  // Tabell-rad-modell (gemensamt schema för admin + pilot)
  // ===================================================================

  /**
   * Diskriminerad union så vi kan rendera olika åtgärder per typ utan
   * att läcka pilot-fält i admin-rader (eller tvärtom). `type`-fältet
   * driver render-logik och sortering.
   */
  type UserRow =
    | {
        type: 'admin';
        sortKey: string;
        userId: string;
        username: string;
        email: string;
        isActive: boolean;
        mfaEnabled: boolean;
        joinedAt: string;
        admin: AdminUser;
      }
    | {
        type: 'pilot';
        sortKey: string;
        userId: string;
        pilotId: string;
        status: PilotAccount['status'];
        joinedAt: string;
        pilot: PilotAccount;
      };

  // ===================================================================
  // List-state
  // ===================================================================

  let admins: AdminUser[] = $state([]);
  let pilots: PilotAccount[] = $state([]);
  let isLoading = $state(true);
  let listError: string | null = $state(null);
  /** Sätts till true om backend rapporterar `mfa_not_configured` på muterande admin-anrop. */
  let mfaGateBlocked = $state(false);

  /**
   * Sortering: typ alfabetiskt först (Admin före Pilot), sedan på
   * joinedAt fallande inom typ. Beräknas via $derived så att tabellen
   * uppdateras reaktivt när admin- eller pilot-listorna ändras.
   */
  const rows: UserRow[] = $derived.by(() => {
    const all: UserRow[] = [];
    for (const a of admins) {
      all.push({
        type: 'admin',
        sortKey: `0:${-new Date(a.joinedAt).getTime()}`,
        userId: a.userId,
        username: a.username,
        email: a.email,
        isActive: a.isActive,
        mfaEnabled: a.mfaEnabled,
        joinedAt: a.joinedAt,
        admin: a,
      });
    }
    for (const p of pilots) {
      all.push({
        type: 'pilot',
        sortKey: `1:${-new Date(p.joinedAt).getTime()}`,
        userId: p.userId,
        pilotId: p.pilotId,
        status: p.status,
        joinedAt: p.joinedAt,
        pilot: p,
      });
    }
    all.sort((x, y) => x.sortKey.localeCompare(y.sortKey));
    return all;
  });

  async function loadAll(): Promise<void> {
    isLoading = true;
    listError = null;
    const [adminResult, pilotResult] = await Promise.all([listAdmins(), listPilotAccounts()]);

    let unauthorized = false;
    let firstError: string | null = null;

    if (adminResult.kind === 'ok') {
      admins = adminResult.items;
    } else if (adminResult.kind === 'unauthorized') {
      admins = [];
      unauthorized = true;
    } else {
      admins = [];
      firstError = adminResult.message;
    }

    if (pilotResult.kind === 'ok') {
      pilots = pilotResult.items;
    } else if (pilotResult.kind === 'unauthorized') {
      pilots = [];
      unauthorized = true;
    } else {
      pilots = [];
      if (firstError === null) firstError = pilotResult.message;
    }

    if (unauthorized) {
      listError = 'Sessionen har gått ut. Logga in igen.';
      onUnauthorized?.();
    } else if (firstError !== null) {
      listError = firstError;
    }

    isLoading = false;
  }

  onMount(() => {
    void loadAll();
  });

  // ===================================================================
  // Invite-admin-modal
  // ===================================================================

  let showInviteModal = $state(false);
  let inviteEmail = $state('');
  let inviteDisplayName = $state('');
  let inviteMfaCode = $state('');
  let inviteError: string | null = $state(null);
  let isInviting = $state(false);
  /**
   * Tempo-lösenord från lyckad invite. Lever bara i $state — försvinner
   * vid modal-unmount via [closeInviteModal] och auto-clear efter 90 s.
   * Lagras ALDRIG i sessionStorage/localStorage (granskningsmönster #2).
   */
  let lastInviteResult: {
    username: string;
    email: string;
    tempPasswordOrLink: string;
  } | null = $state(null);
  let copiedToClipboard = $state(false);

  let inviteDisplayNameInput: HTMLInputElement | undefined = $state();
  let inviteCloseButton: HTMLButtonElement | undefined = $state();

  $effect(() => {
    if (showInviteModal && lastInviteResult && inviteCloseButton) {
      queueMicrotask(() => inviteCloseButton?.focus());
    } else if (showInviteModal && !lastInviteResult && inviteDisplayNameInput) {
      queueMicrotask(() => inviteDisplayNameInput?.focus());
    }
  });

  // Auto-clear-timer för tempo-lösenord (UX-vakt-fix). Lösenordet rensas
  // automatiskt 90 sekunder efter visning som privacy-skydd om admin
  // lämnar skärmen olåst. Reviewer-fynd från PR #154.
  let inviteResultTimerId: number | null = null;
  const INVITE_RESULT_TIMEOUT_MS = 90_000;

  function clearInviteResultTimer(): void {
    if (inviteResultTimerId !== null) {
      window.clearTimeout(inviteResultTimerId);
      inviteResultTimerId = null;
    }
  }

  function startInviteResultTimer(): void {
    clearInviteResultTimer();
    inviteResultTimerId = window.setTimeout(() => {
      inviteResultTimerId = null;
      lastInviteResult = null;
      showInviteModal = false;
    }, INVITE_RESULT_TIMEOUT_MS);
  }

  function openInviteModal(): void {
    showInviteModal = true;
    inviteEmail = '';
    inviteDisplayName = '';
    inviteMfaCode = '';
    inviteError = null;
    lastInviteResult = null;
    copiedToClipboard = false;
    clearInviteResultTimer();
  }

  function closeInviteModal(): void {
    // Block backdrop-close + Escape när tempo-lösenord visas (UX-vakt-fix
    // PR #154): admin ska inte oavsiktligt tappa engångslösenordet via
    // utanför-klick. Kräver explicit klick på "Stäng"-knappen.
    if (lastInviteResult !== null) {
      return;
    }
    forceCloseInviteModal();
  }

  function forceCloseInviteModal(): void {
    showInviteModal = false;
    inviteEmail = '';
    inviteDisplayName = '';
    inviteMfaCode = '';
    inviteError = null;
    // CRITICAL: nollställ tempo-lösenord när modalen stängs så det inte
    // ligger kvar i runtime-state.
    lastInviteResult = null;
    copiedToClipboard = false;
    clearInviteResultTimer();
  }

  function validateInviteForm(): string | null {
    const trimmedName = inviteDisplayName.trim();
    const trimmedEmail = inviteEmail.trim();
    if (trimmedName.length === 0) return 'Visningsnamn krävs.';
    if (trimmedName.length > 100) return 'Visningsnamn får vara högst 100 tecken.';
    if (trimmedEmail.length === 0) return 'E-post krävs.';
    if (!trimmedEmail.includes('@') || !trimmedEmail.includes('.')) {
      return 'Ogiltig e-postadress.';
    }
    if (inviteMfaCode.trim().length !== 6 || !/^\d{6}$/.test(inviteMfaCode.trim())) {
      return 'MFA-koden består av 6 siffror.';
    }
    return null;
  }

  async function submitInvite(): Promise<void> {
    inviteError = null;
    const validation = validateInviteForm();
    if (validation) {
      inviteError = validation;
      return;
    }

    isInviting = true;
    try {
      const result = await inviteAdmin(
        inviteEmail.trim(),
        inviteDisplayName.trim(),
        inviteMfaCode.trim(),
      );
      if (result.kind === 'ok') {
        lastInviteResult = {
          username: result.username,
          email: result.email,
          tempPasswordOrLink: result.tempPasswordOrLink,
        };
        startInviteResultTimer();
        // Rensa form-värden så MFA-koden inte ligger kvar i DOM.
        inviteEmail = '';
        inviteDisplayName = '';
        inviteMfaCode = '';
        await loadAll();
      } else if (result.kind === 'mfa_not_configured') {
        mfaGateBlocked = true;
        forceCloseInviteModal();
      } else if (result.kind === 'unauthorized') {
        inviteError = 'Sessionen har gått ut. Logga in igen.';
        onUnauthorized?.();
      } else if (result.kind === 'invalid_mfa_code') {
        inviteError = 'Felaktig MFA-kod. Försök igen.';
      } else if (result.kind === 'mfa_locked') {
        inviteError = 'För många misslyckade MFA-försök. Vänta en stund och försök igen.';
      } else if (result.kind === 'duplicate_identifier') {
        inviteError = 'Användarnamn eller e-post är redan registrerat.';
      } else {
        inviteError = result.message;
      }
    } finally {
      isInviting = false;
    }
  }

  async function copyTempPassword(): Promise<void> {
    if (!lastInviteResult) return;
    try {
      await navigator.clipboard.writeText(lastInviteResult.tempPasswordOrLink);
      copiedToClipboard = true;
      setTimeout(() => {
        copiedToClipboard = false;
      }, 2000);
    } catch {
      copiedToClipboard = false;
    }
  }

  // ===================================================================
  // Inaktivera-admin-modal (med MFA-step-up)
  // ===================================================================

  let pendingDeactivateAdmin: AdminUser | null = $state(null);
  let deactivateAdminMfaCode = $state('');
  let deactivateAdminError: string | null = $state(null);
  let isDeactivatingAdmin = $state(false);
  let deactivateAdminMfaInput: HTMLInputElement | undefined = $state();

  $effect(() => {
    if (pendingDeactivateAdmin && deactivateAdminMfaInput) {
      queueMicrotask(() => deactivateAdminMfaInput?.focus());
    }
  });

  function startDeactivateAdmin(admin: AdminUser): void {
    pendingDeactivateAdmin = admin;
    deactivateAdminMfaCode = '';
    deactivateAdminError = null;
  }

  function cancelDeactivateAdmin(): void {
    if (isDeactivatingAdmin) return;
    pendingDeactivateAdmin = null;
    deactivateAdminMfaCode = '';
    deactivateAdminError = null;
  }

  async function confirmDeactivateAdmin(): Promise<void> {
    if (!pendingDeactivateAdmin) return;
    deactivateAdminError = null;
    if (
      deactivateAdminMfaCode.trim().length !== 6 ||
      !/^\d{6}$/.test(deactivateAdminMfaCode.trim())
    ) {
      deactivateAdminError = 'MFA-koden består av 6 siffror.';
      return;
    }
    isDeactivatingAdmin = true;
    try {
      const result = await deactivateAdmin(
        pendingDeactivateAdmin.userId,
        deactivateAdminMfaCode.trim(),
      );
      if (result.kind === 'ok') {
        pendingDeactivateAdmin = null;
        deactivateAdminMfaCode = '';
        await loadAll();
      } else if (result.kind === 'mfa_not_configured') {
        mfaGateBlocked = true;
        cancelDeactivateAdmin();
      } else if (result.kind === 'unauthorized') {
        deactivateAdminError = 'Sessionen har gått ut. Logga in igen.';
        onUnauthorized?.();
      } else if (result.kind === 'invalid_mfa_code') {
        deactivateAdminError = 'Felaktig MFA-kod. Försök igen.';
      } else if (result.kind === 'mfa_locked') {
        deactivateAdminError = 'För många misslyckade MFA-försök. Vänta en stund och försök igen.';
      } else if (result.kind === 'self_deactivation_forbidden') {
        deactivateAdminError = 'Du kan inte inaktivera ditt eget admin-konto.';
      } else if (result.kind === 'not_found') {
        deactivateAdminError = 'Admin-kontot kunde inte hittas eller är redan inaktivt.';
      } else {
        deactivateAdminError = result.message;
      }
    } finally {
      isDeactivatingAdmin = false;
    }
  }

  // ===================================================================
  // Skapa-pilotkonto-modal (med engångskod)
  // ===================================================================

  let isCreatingPilot = $state(false);
  let createPilotError: string | null = $state(null);
  let createdPilotAccount: CreatePilotAccountSuccess | null = $state(null);
  let pilotCopyState: 'idle' | 'copied' | 'failed' = $state('idle');
  let deepLinkCopyState: 'idle' | 'copied' | 'failed' = $state('idle');
  let smsCopyState: 'idle' | 'copied' | 'failed' = $state('idle');
  let qrCodeDataUrl: string | null = $state(null);
  let createdPilotCloseButton: HTMLButtonElement | undefined = $state();

  /**
   * Deep-link URL för pilot-aktivering (S-13-05). Genereras client-side
   * från pilot-id + activation-code. Android-app:n parsar URL:en när
   * användaren tappar länken — fält pre-fylls i onboarding och admin
   * bekräftar med "Aktivera"-knappen.
   */
  // $derived.by + lokal const, inte $derived(expr): svelte-check 4.4.6 narrar inte
  // $state-variabler korrekt inuti expression-form (rapporterar `never` på fältåtkomst).
  let pilotDeepLinkUrl = $derived.by(() => {
    const acc = createdPilotAccount;
    return acc
      ? buildPilotActivationDeepLink(acc.pilotId, acc.activationCode)
      : null;
  });

  /**
   * Pre-formaterad SMS-text. Admin kan kopiera och klistra in i SMS,
   * Signal eller iMessage. Texten är optimerad för 1 SMS-segment vid
   * standard pilot_id/code-längder.
   */
  let pilotSmsText = $derived(
    pilotDeepLinkUrl ? buildPilotActivationSmsText(pilotDeepLinkUrl) : null,
  );

  /**
   * QR-kod genereras client-side via `qrcode`-bibliotekt. Renderas som
   * data-URL så vi inte behöver skicka något till backend. Storlek
   * 200x200 för rimlig läsbarhet på papper-utskrift och skärm.
   */
  $effect(() => {
    const url = pilotDeepLinkUrl;
    if (!url) {
      qrCodeDataUrl = null;
      return;
    }
    QRCode.toDataURL(url, { width: 200, margin: 1 }).then(
      (dataUrl) => {
        qrCodeDataUrl = dataUrl;
      },
      () => {
        // Tyst fail — admin kan fortfarande använda kopiera-knapparna.
        qrCodeDataUrl = null;
      },
    );
  });
  // Bekräftelse-modal innan backend-anrop (Jonas-feedback 2026-05-05:
  // "Skapa pilotkonto är lite för aggresiv, det skapas direkt när man
  // klicka på knappen, det borde väl göras när man väljer skapa"). Det
  // skyddar mot råkliv: själva backend-anropet konsumerar en engångskod
  // som måste hanteras direkt och inte kan ångras utan att skapa ett
  // nytt konto.
  let showCreatePilotConfirm = $state(false);
  let createPilotConfirmButton: HTMLButtonElement | undefined = $state();

  // S-02-12 + S-04-31: admin-only kontakt-info som följer med vid
  // kontoskapande. Datakategori "Kontaktuppgifter (admin-only)",
  // rättslig grund Berättigat intresse, retention CASCADE-DELETE.
  // Mobilappen har INGEN åtkomst — endast Lyktan via admin-MFA-skyddade
  // endpoints. Audit-logg sparar bara fält-NAMN som ändrats, inte värden.
  let createContactName = $state('');
  let createContactPhone = $state('');
  let createContactEmail = $state('');

  $effect(() => {
    if (createdPilotAccount && createdPilotCloseButton) {
      queueMicrotask(() => createdPilotCloseButton?.focus());
    }
  });

  $effect(() => {
    if (showCreatePilotConfirm && createPilotConfirmButton) {
      queueMicrotask(() => createPilotConfirmButton?.focus());
    }
  });

  function openCreatePilotConfirm(): void {
    if (isCreatingPilot) return;
    createPilotError = null;
    // Återställ kontakt-fälten varje gång modalen öppnas — admin ska
    // aldrig kunna se eller återanvända föregående pilots uppgifter.
    createContactName = '';
    createContactPhone = '';
    createContactEmail = '';
    showCreatePilotConfirm = true;
  }

  function cancelCreatePilotConfirm(): void {
    if (isCreatingPilot) return;
    createContactName = '';
    createContactPhone = '';
    createContactEmail = '';
    showCreatePilotConfirm = false;
  }

  async function confirmAndCreatePilot(): Promise<void> {
    if (isCreatingPilot) return;
    createPilotError = null;

    // S-02-12: validera kontakt-fält klient-side innan submit. name är
    // obligatoriskt; phone/email valfria. Backend re-validerar alltid.
    const validation = validateUserContacts({
      name: createContactName,
      phone: createContactPhone,
      email: createContactEmail,
    });
    if (validation) {
      createPilotError = validation.message;
      return;
    }

    isCreatingPilot = true;
    try {
      const result = await createPilotAccount({
        name: createContactName.trim(),
        phone: createContactPhone.trim() || undefined,
        email: createContactEmail.trim() || undefined,
      });
      if (result.kind === 'created') {
        createdPilotAccount = result;
        pilotCopyState = 'idle';
        showCreatePilotConfirm = false;
        // Rensa kontakt-fälten efter framgångsrik submit så de inte
        // ligger kvar i DOM:en.
        createContactName = '';
        createContactPhone = '';
        createContactEmail = '';
        await loadAll();
      } else {
        createPilotError = result.message;
        // Behåll bekräftelse-modalen öppen så admin ser felet och kan
        // försöka igen eller avbryta. Felet är kategorisk text från
        // klient-discriminated-union (ingen fritext-PII).
      }
    } finally {
      isCreatingPilot = false;
    }
  }

  async function copyPilotCode(): Promise<void> {
    if (!createdPilotAccount) return;
    try {
      await navigator.clipboard.writeText(createdPilotAccount.activationCode);
      pilotCopyState = 'copied';
    } catch {
      pilotCopyState = 'failed';
    }
  }

  async function copyDeepLink(): Promise<void> {
    if (!pilotDeepLinkUrl) return;
    try {
      await navigator.clipboard.writeText(pilotDeepLinkUrl);
      deepLinkCopyState = 'copied';
    } catch {
      deepLinkCopyState = 'failed';
    }
  }

  async function copySmsText(): Promise<void> {
    if (!pilotSmsText) return;
    try {
      await navigator.clipboard.writeText(pilotSmsText);
      smsCopyState = 'copied';
    } catch {
      smsCopyState = 'failed';
    }
  }

  function dismissCreatedPilot(): void {
    // Hard-rule: koden får aldrig finnas kvar efter att admin stängt
    // dialogen — backend kan inte återskapa den. Samma princip för
    // deep-link och QR-kod (de innehåller koden).
    createdPilotAccount = null;
    pilotCopyState = 'idle';
    deepLinkCopyState = 'idle';
    smsCopyState = 'idle';
    qrCodeDataUrl = null;
  }

  // ===================================================================
  // Inaktivera-pilotkonto-modal (enkel bekräftelse, ingen MFA)
  // ===================================================================

  let pendingDeactivatePilot: PilotAccount | null = $state(null);
  let deactivatePilotError: string | null = $state(null);
  let isDeactivatingPilot = $state(false);

  function startDeactivatePilot(pilot: PilotAccount): void {
    pendingDeactivatePilot = pilot;
    deactivatePilotError = null;
  }

  function cancelDeactivatePilot(): void {
    if (isDeactivatingPilot) return;
    pendingDeactivatePilot = null;
    deactivatePilotError = null;
  }

  async function confirmDeactivatePilot(): Promise<void> {
    if (!pendingDeactivatePilot) return;
    deactivatePilotError = null;
    isDeactivatingPilot = true;
    try {
      const result = await deactivatePilotAccount(pendingDeactivatePilot.userId);
      if (result.kind === 'ok') {
        pendingDeactivatePilot = null;
        await loadAll();
      } else if (result.kind === 'unauthorized') {
        deactivatePilotError = 'Sessionen har gått ut. Logga in igen.';
        onUnauthorized?.();
      } else if (result.kind === 'not_found') {
        deactivatePilotError = 'Pilotkontot kunde inte hittas eller är redan inaktivt.';
      } else {
        deactivatePilotError = result.message;
      }
    } finally {
      isDeactivatingPilot = false;
    }
  }

  // ===================================================================
  // Redigera-pilot-modal (S-04-31-followup, Jonas-bug 2026-05-08)
  // ---
  // Öppnas via "Redigera"-knappen på pilot-rader. Hämtar existing
  // contacts via GET /api/admin/users/{userId}/contacts vid öppning,
  // pre-fyller fälten och submittar via PUT. 404 från GET tolkas som
  // "tomt formulär" (samma flöde som vid skapande utan contacts).
  //
  // Hard-rules:
  //  - Inga råa databas-ID:n — userId är UUID från klient-modellen.
  //  - Audit på backend loggar bara fält-namn som ändrats, INTE värden
  //    (samma policy som S-04-31, ärvs via PUT-endpoint).
  //  - Fälten lever bara i $state — inget sessionStorage/localStorage.
  // ===================================================================

  let pendingEditPilot: PilotAccount | null = $state(null);
  let editContactName = $state('');
  let editContactPhone = $state('');
  let editContactEmail = $state('');
  let editError: string | null = $state(null);
  let isLoadingEditContacts = $state(false);
  let isSavingEditContacts = $state(false);
  /** Visas som transient toast efter lyckad save. */
  let editSaveSuccessMessage: string | null = $state(null);
  let editSaveSuccessTimerId: number | null = null;
  let editContactNameInput: HTMLInputElement | undefined = $state();

  $effect(() => {
    if (pendingEditPilot && !isLoadingEditContacts && editContactNameInput) {
      queueMicrotask(() => editContactNameInput?.focus());
    }
  });

  function clearEditSaveSuccessTimer(): void {
    if (editSaveSuccessTimerId !== null) {
      window.clearTimeout(editSaveSuccessTimerId);
      editSaveSuccessTimerId = null;
    }
  }

  function showEditSaveSuccess(): void {
    clearEditSaveSuccessTimer();
    editSaveSuccessMessage = 'Uppgifter sparade';
    editSaveSuccessTimerId = window.setTimeout(() => {
      editSaveSuccessMessage = null;
      editSaveSuccessTimerId = null;
    }, 4000);
  }

  function resetEditFormState(): void {
    editContactName = '';
    editContactPhone = '';
    editContactEmail = '';
    editError = null;
  }

  async function startEditPilot(pilot: PilotAccount): Promise<void> {
    pendingEditPilot = pilot;
    resetEditFormState();
    isLoadingEditContacts = true;
    try {
      const result = await getUserContacts(pilot.userId);
      if (result.kind === 'ok') {
        editContactName = result.contacts.name;
        editContactPhone = result.contacts.phone ?? '';
        editContactEmail = result.contacts.email ?? '';
      } else if (result.kind === 'not_found') {
        // Inga existing contacts — öppna tomt formulär (samma flöde som
        // vid skapande). Visa INTE error-toast.
        resetEditFormState();
      } else if (result.kind === 'unauthorized') {
        pendingEditPilot = null;
        onUnauthorized?.();
      } else {
        // Network/server-fel — håll modalen öppen och visa felmeddelande.
        editError = 'Kunde inte hämta — försök igen.';
      }
    } finally {
      isLoadingEditContacts = false;
    }
  }

  function cancelEditPilot(): void {
    if (isSavingEditContacts) return;
    pendingEditPilot = null;
    resetEditFormState();
  }

  async function confirmEditPilot(): Promise<void> {
    if (!pendingEditPilot || isSavingEditContacts) return;
    editError = null;

    // Klient-side validering matchar backend-CHECK. Backend re-validerar.
    const validation = validateUserContacts({
      name: editContactName,
      phone: editContactPhone,
      email: editContactEmail,
    });
    if (validation) {
      editError = validation.message;
      return;
    }

    isSavingEditContacts = true;
    try {
      const result = await updateUserContacts(pendingEditPilot.userId, {
        name: editContactName,
        phone: editContactPhone,
        email: editContactEmail,
      });
      if (result.kind === 'ok') {
        pendingEditPilot = null;
        resetEditFormState();
        showEditSaveSuccess();
        await loadAll();
      } else if (result.kind === 'invalid') {
        editError = result.message;
      } else if (result.kind === 'unauthorized') {
        pendingEditPilot = null;
        onUnauthorized?.();
      } else if (result.kind === 'not_found') {
        editError = 'Pilotkontot kunde inte hittas.';
      } else {
        editError = result.message;
      }
    } finally {
      isSavingEditContacts = false;
    }
  }

  // ===================================================================
  // Cleanup vid unmount
  // ===================================================================

  onDestroy(() => {
    clearInviteResultTimer();
    clearEditSaveSuccessTimer();
    lastInviteResult = null;
    createdPilotAccount = null;
    pendingEditPilot = null;
  });

  // ===================================================================
  // Pilot-id-presentation (förkorta för bättre läsbarhet)
  // ===================================================================

  function formatPilotId(pilotId: string): string {
    // Bevara hela ID:t men visa det i monospace-tabell-cellen för att
    // signalera att det är en kategorisk identifierare. Vi förkortar
    // INTE — admin kan behöva kopiera fullt ID för support-spårning.
    return pilotId;
  }

  function pilotStatusLabel(status: PilotAccount['status']): string {
    switch (status) {
      case 'pending':
        return 'Väntande';
      case 'activated':
        return 'Aktiv';
      case 'expired':
        return 'Utgången';
      default:
        return 'Okänd';
    }
  }
</script>

<section class="users-section" aria-label="Användare">
  {#if mfaGateBlocked}
    <div class="alert mfa-gate" role="alert">
      <strong>Tvåfaktor krävs.</strong>
      Du måste aktivera tvåfaktor på ditt eget konto innan du kan bjuda in eller inaktivera andra admins.
      {#if onMfaGateOpen}
        <button type="button" class="link-button" onclick={onMfaGateOpen}>
          Aktivera tvåfaktor nu
        </button>
      {:else}
        Öppna <strong>Konto och säkerhet</strong> och kom tillbaka hit.
      {/if}
    </div>
  {/if}

  <div class="toolbar">
    <button
      type="button"
      class="btn primary"
      onclick={openInviteModal}
      disabled={isLoading || mfaGateBlocked}
    >
      Lägg till administratör
    </button>
    <button
      type="button"
      class="btn primary"
      onclick={openCreatePilotConfirm}
      disabled={isLoading || isCreatingPilot}
    >
      {isCreatingPilot ? 'Skapar användare…' : 'Skapa användare'}
    </button>
    <button
      type="button"
      class="icon-btn icon-btn-secondary"
      onclick={() => void loadAll()}
      disabled={isLoading}
      aria-label={isLoading ? 'Laddar' : 'Uppdatera lista'}
      title={isLoading ? 'Laddar…' : 'Uppdatera'}
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
        stroke-linecap="round"
        stroke-linejoin="round"
        class:spinning={isLoading}
      >
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
      </svg>
    </button>
  </div>

  {#if listError}
    <div class="alert error" role="alert">{listError}</div>
  {/if}

  {#if createPilotError}
    <div class="alert error" role="alert">{createPilotError}</div>
  {/if}

  {#if editSaveSuccessMessage}
    <div class="alert info" role="status" aria-live="polite" data-testid="edit-save-success">
      {editSaveSuccessMessage}
    </div>
  {/if}

  {#if !isLoading && rows.length === 0 && !listError}
    <div class="empty">
      <p class="kicker">Inga användare än.</p>
      <p>
        Klicka på <strong>Lägg till administratör</strong> eller <strong>Skapa användare</strong> för att lägga
        till den första användaren.
      </p>
    </div>
  {:else if rows.length > 0}
    <div class="table-wrap">
      <table class="users-table">
        <thead>
          <tr>
            <th scope="col">Typ</th>
            <th scope="col">Identifierare</th>
            <th scope="col">Status</th>
            <th scope="col">Skapad</th>
            <th scope="col" class="actions-col">Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {#each rows as row (row.userId)}
            {#if row.type === 'admin'}
              <tr class:inactive={!row.isActive}>
                <td>
                  <span class="type-badge type-admin">Admin</span>
                </td>
                <td class="identifier-cell">
                  <span class="identifier-row">
                    <span class="primary-id">
                      {row.username}
                      {#if row.userId === currentUserId}
                        <span class="self-badge" aria-label="Detta är ditt konto">Du</span>
                      {/if}
                    </span>
                    <span class="secondary-id">{row.email}</span>
                  </span>
                </td>
                <td>
                  <span class="status-row">
                    {#if row.isActive}
                      <span class="status status-active">Aktiv</span>
                    {:else}
                      <span class="status status-inactive">Inaktiv</span>
                    {/if}
                    {#if row.mfaEnabled}
                      <span class="mfa-badge mfa-yes" aria-label="Tvåfaktor aktiverad">
                        ✓ MFA
                      </span>
                    {:else}
                      <span class="mfa-badge mfa-no" aria-label="Tvåfaktor saknas"> ✗ MFA </span>
                    {/if}
                  </span>
                </td>
                <td class="dim">{formatDate(row.joinedAt)}</td>
                <td class="actions-cell">
                  {#if row.isActive && row.userId !== currentUserId}
                    <button
                      type="button"
                      class="btn small danger"
                      onclick={() => startDeactivateAdmin(row.admin)}
                      disabled={mfaGateBlocked}
                    >
                      Inaktivera
                    </button>
                  {/if}
                </td>
              </tr>
            {:else}
              <tr class:inactive={row.status === 'expired'}>
                <td>
                  <span class="type-badge type-pilot">Pilot</span>
                </td>
                <td class="identifier-cell">
                  <code class="primary-id mono">{formatPilotId(row.pilotId)}</code>
                </td>
                <td>
                  {#if row.status === 'activated'}
                    <span class="status status-active">Aktiv</span>
                  {:else if row.status === 'pending'}
                    <span class="status status-pending">Väntande</span>
                  {:else if row.status === 'expired'}
                    <span class="status status-inactive">Utgången</span>
                  {:else}
                    <span class="status status-inactive">{pilotStatusLabel(row.status)}</span>
                  {/if}
                </td>
                <td class="dim">{formatDate(row.joinedAt)}</td>
                <td class="actions-cell">
                  <button
                    type="button"
                    class="btn small secondary"
                    onclick={() => void startEditPilot(row.pilot)}
                    data-testid="edit-pilot-button"
                  >
                    Redigera
                  </button>
                  <button
                    type="button"
                    class="btn small danger"
                    onclick={() => startDeactivatePilot(row.pilot)}
                  >
                    Inaktivera
                  </button>
                </td>
              </tr>
            {/if}
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
</section>

<!-- ============================================================
     Modal: Lägg till administratör (form + tempo-lösenord-resultat)
     ============================================================ -->
{#if showInviteModal}
  <div
    class="modal-backdrop"
    role="presentation"
    onclick={closeInviteModal}
    onkeydown={(e) => {
      if (e.key === 'Escape') closeInviteModal();
    }}
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-title"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
    >
      {#if !lastInviteResult}
        <h3 id="invite-title">Lägg till administratör</h3>
        <p class="dim">
          Kontot skapas med ett engångslösenord som mottagaren måste byta vid första inloggningen.
        </p>
        <form
          class="form"
          onsubmit={(e) => {
            e.preventDefault();
            void submitInvite();
          }}
        >
          <div class="field">
            <label for="invite-displayname">Visningsnamn</label>
            <input
              id="invite-displayname"
              type="text"
              bind:value={inviteDisplayName}
              bind:this={inviteDisplayNameInput}
              maxlength="100"
              required
              autocomplete="off"
              placeholder="t.ex. sara"
            />
            <p class="field-hint">
              Kort namn för administratörens konto, t.ex. förnamn eller initialer. Max 100 tecken.
              Använd inte beskrivande text om personen.
            </p>
          </div>
          <div class="field">
            <label for="invite-email">E-post</label>
            <input
              id="invite-email"
              type="email"
              bind:value={inviteEmail}
              maxlength="255"
              required
              autocomplete="off"
            />
          </div>
          <div class="field">
            <label for="invite-mfa">Din MFA-kod (TOTP)</label>
            <input
              id="invite-mfa"
              type="text"
              inputmode="numeric"
              pattern="[0-9]{'{6}'}"
              maxlength="6"
              bind:value={inviteMfaCode}
              required
              autocomplete="one-time-code"
              placeholder="123456"
            />
            <p class="field-hint">
              Sex siffror från din authenticator-app. Krävs för att skydda inbjudningen mot stulna
              cookies.
            </p>
          </div>

          {#if inviteError}
            <div class="alert error" role="alert">{inviteError}</div>
          {/if}

          <div class="modal-actions">
            <button
              type="button"
              class="btn secondary"
              onclick={closeInviteModal}
              disabled={isInviting}
            >
              Avbryt
            </button>
            <button type="submit" class="btn primary" disabled={isInviting}>
              {isInviting ? 'Skickar…' : 'Bjud in'}
            </button>
          </div>
        </form>
      {:else}
        <h3 id="invite-title">Engångslösenord skapat</h3>
        <p>
          Kontot för <strong>{lastInviteResult.username}</strong>
          ({lastInviteResult.email}) är skapat. Spara engångslösenordet nedan och skicka det till
          mottagaren via en säker kanal — Signal eller fysiskt papper. Mottagaren tvingas byta
          lösenordet vid första inloggningen.
        </p>
        <div class="alert info" role="note">
          <strong>Detta visas bara nu.</strong> När du stänger dialogen försvinner lösenordet permanent.
          Det lagras inte i Lyktan eller webbläsaren. Av säkerhetsskäl rensas det automatiskt efter 90
          sekunder.
        </div>
        <div class="temp-password-box">
          <span class="temp-password-label">Engångslösenord</span>
          <code class="temp-password">{lastInviteResult.tempPasswordOrLink}</code>
        </div>
        <div class="modal-actions">
          <button type="button" class="btn secondary" onclick={() => void copyTempPassword()}>
            {copiedToClipboard ? 'Kopierat!' : 'Kopiera till urklipp'}
          </button>
          <button
            type="button"
            class="btn primary"
            onclick={forceCloseInviteModal}
            bind:this={inviteCloseButton}
          >
            Stäng
          </button>
        </div>
      {/if}
    </div>
  </div>
{/if}

<!-- ============================================================
     Modal: Inaktivera admin (kräver MFA-step-up)
     ============================================================ -->
{#if pendingDeactivateAdmin}
  <div
    class="modal-backdrop"
    role="presentation"
    onclick={cancelDeactivateAdmin}
    onkeydown={(e) => {
      if (e.key === 'Escape') cancelDeactivateAdmin();
    }}
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deactivate-admin-title"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
    >
      <h3 id="deactivate-admin-title">Inaktivera admin</h3>
      <p>
        Kontot <strong>{pendingDeactivateAdmin.username}</strong>
        ({pendingDeactivateAdmin.email}) inaktiveras och kan inte längre logga in i Lyktan. Audit-
        historiken bevaras spårbart i plattformen — ingenting raderas i tysthet.
      </p>
      <form
        class="form"
        onsubmit={(e) => {
          e.preventDefault();
          void confirmDeactivateAdmin();
        }}
      >
        <div class="field">
          <label for="deactivate-admin-mfa">Din MFA-kod (TOTP)</label>
          <input
            id="deactivate-admin-mfa"
            type="text"
            inputmode="numeric"
            pattern="[0-9]{'{6}'}"
            maxlength="6"
            bind:value={deactivateAdminMfaCode}
            bind:this={deactivateAdminMfaInput}
            required
            autocomplete="one-time-code"
            placeholder="123456"
          />
          <p class="field-hint">
            Sex siffror från din authenticator-app. Krävs för att skydda mot stulna cookies.
          </p>
        </div>

        {#if deactivateAdminError}
          <div class="alert error" role="alert">{deactivateAdminError}</div>
        {/if}

        <div class="modal-actions">
          <button
            type="button"
            class="btn secondary"
            onclick={cancelDeactivateAdmin}
            disabled={isDeactivatingAdmin}
          >
            Avbryt
          </button>
          <button type="submit" class="btn danger" disabled={isDeactivatingAdmin}>
            {isDeactivatingAdmin ? 'Inaktiverar…' : 'Inaktivera'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- ============================================================
     Modal: Bekräfta skapande av nytt användarkonto (pilot)
     Lägger en confirm-grind innan backend-anropet konsumerar en
     engångskod som inte kan ångras (Jonas-feedback 2026-05-05:
     "skapa pilotkonto är lite för aggresiv"). Backdrop-klick + Esc
     stänger så länge backend inte är aktivt.
     ============================================================ -->
{#if showCreatePilotConfirm}
  <div
    class="modal-backdrop"
    role="presentation"
    onclick={cancelCreatePilotConfirm}
    onkeydown={(e) => {
      if (e.key === 'Escape') cancelCreatePilotConfirm();
    }}
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-pilot-confirm-title"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
    >
      <h3 id="create-pilot-confirm-title">Skapa nytt användarkonto?</h3>
      <p>
        Klick på <strong>Skapa</strong> genererar ett nytt användarkonto med en
        <strong>engångskod</strong> som visas en gång. Koden kan inte hämtas igen — du behöver
        skicka den till deltagaren via säker kanal direkt efter skapandet.
      </p>
      <p class="dim">
        Engångskoden går ut efter ett dygn om kontot inte aktiveras.
      </p>

      <!-- S-02-12 + S-04-31: admin-only kontaktuppgifter. Endast Compuna
           administratörer ser dessa fält. Mobilappen har ingen åtkomst. -->
      <form
        class="form contact-form"
        onsubmit={(e) => {
          e.preventDefault();
          void confirmAndCreatePilot();
        }}
      >
        <div class="field">
          <label for="create-contact-name">Namn (obligatoriskt)</label>
          <input
            id="create-contact-name"
            type="text"
            bind:value={createContactName}
            maxlength="200"
            required
            autocomplete="off"
            placeholder="t.ex. Anna Andersson"
            disabled={isCreatingPilot}
          />
        </div>
        <div class="field">
          <label for="create-contact-phone">Mobilnummer (valfritt)</label>
          <input
            id="create-contact-phone"
            type="text"
            bind:value={createContactPhone}
            maxlength="20"
            autocomplete="off"
            placeholder="+46701234567"
            disabled={isCreatingPilot}
          />
          <p class="field-hint">Format: +landskod + nummer (t.ex. +46701234567).</p>
        </div>
        <div class="field">
          <label for="create-contact-email">E-post (valfritt)</label>
          <input
            id="create-contact-email"
            type="email"
            bind:value={createContactEmail}
            maxlength="255"
            autocomplete="off"
            placeholder="anna@example.se"
            disabled={isCreatingPilot}
          />
        </div>
        <p class="contact-disclosure">
          Compuna AB lagrar dessa uppgifter för pilot-administration. Du kan begära radering via
          <a href="mailto:jonas@compuna.se">jonas@compuna.se</a>
          eller genom att avsluta deltagandet i appen.
        </p>

        {#if createPilotError}
          <div class="alert error" role="alert">{createPilotError}</div>
        {/if}

        <div class="modal-actions">
          <button
            type="button"
            class="btn secondary"
            onclick={cancelCreatePilotConfirm}
            disabled={isCreatingPilot}
          >
            Avbryt
          </button>
          <button
            type="submit"
            class="btn primary"
            disabled={isCreatingPilot}
            bind:this={createPilotConfirmButton}
          >
            {isCreatingPilot ? 'Skapar…' : 'Skapa'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<!-- ============================================================
     Modal: Engångskod för nyskapat pilotkonto
     Backdrop saknar avsiktligt onclick-handler — modalen stängs
     ENDAST via "Jag har skickat koden — stäng"-knappen så att
     kodförlust kräver medvetet val (S-04-16 säkerhetsfix).
     ============================================================ -->
{#if createdPilotAccount}
  <div class="modal-backdrop" role="presentation">
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pilot-created-rubrik"
      tabindex="-1"
    >
      <h3 id="pilot-created-rubrik">Pilotkonto skapat</h3>
      <p class="warning" role="alert">
        Engångskoden visas <strong>endast nu</strong>. Den kan inte hämtas igen efter att du stänger
        dialogen. Föredra Signal/iMessage (krypterad kanal) eller fysisk QR-utskrift.
        SMS funkar för pilot — koden har kort giltighet och kan revokeras vid behov.
      </p>

      <dl class="details">
        <dt>Konto-id</dt>
        <dd>
          <code>{createdPilotAccount.pilotId}</code>
        </dd>

        <dt>Engångskod</dt>
        <dd class="code-row">
          <code class="activation-code">{createdPilotAccount.activationCode}</code>
          <button type="button" class="btn secondary" onclick={() => void copyPilotCode()}>
            {pilotCopyState === 'copied' ? 'Kopierad' : 'Kopiera'}
          </button>
        </dd>

        <dt>Giltig till</dt>
        <dd>{formatDate(createdPilotAccount.expiresAt)}</dd>

        <!-- S-13-05: Deep-link för one-tap-aktivering i Android-app:n.
             Pilot-deltagaren tappar URL:en → app öppnas → fält pre-fylls
             → bekräftar med "Aktivera". Detsamma som engångskoden men
             smidigare distribution (ingen manuell inskrivning). -->
        <dt>Aktiverings-länk</dt>
        <dd class="code-row">
          <code class="deep-link" data-testid="pilot-deep-link">{pilotDeepLinkUrl}</code>
          <button type="button" class="btn secondary" onclick={() => void copyDeepLink()}>
            {deepLinkCopyState === 'copied' ? 'Kopierad' : 'Kopiera URL'}
          </button>
        </dd>

        <!-- Pre-formaterad SMS-text — admin kan kopiera och klistra
             direkt i SMS/Signal/iMessage. Texten innehåller välkomst-fras
             + URL och håller sig under 320 tecken (max 2 SMS-segment). -->
        <dt>SMS-text</dt>
        <dd class="code-row">
          <code class="sms-text">{pilotSmsText}</code>
          <button type="button" class="btn secondary" onclick={() => void copySmsText()}>
            {smsCopyState === 'copied' ? 'Kopierad' : 'Kopiera SMS'}
          </button>
        </dd>

        <!-- QR-kod genererad client-side via qrcode-lib. Användbar för
             fysisk utskrift eller skärmdelning — pilot-deltagaren skannar
             med telefonkamera → öppnar Härden direkt. -->
        {#if qrCodeDataUrl}
          <dt>QR-kod</dt>
          <dd class="qr-row">
            <img
              src={qrCodeDataUrl}
              alt="QR-kod för pilot-aktivering. Skanna med telefonkamera."
              width="200"
              height="200"
              data-testid="pilot-qr-code"
            />
            <p class="qr-hint">
              Skanna med pilot-deltagarens telefonkamera för att öppna Härden direkt.
            </p>
          </dd>
        {/if}
      </dl>

      {#if pilotCopyState === 'copied' || deepLinkCopyState === 'copied' || smsCopyState === 'copied'}
        <p class="copy-status copied" aria-live="polite">
          Innehåll ligger nu i urklipp. Glöm inte att rensa urklippet efter att du skickat det.
        </p>
      {:else if pilotCopyState === 'failed' || deepLinkCopyState === 'failed' || smsCopyState === 'failed'}
        <p class="copy-status failed" aria-live="polite">
          Kunde inte kopiera automatiskt — markera texten ovan och kopiera manuellt.
        </p>
      {/if}

      <div class="modal-actions">
        <button
          type="button"
          class="btn primary"
          onclick={dismissCreatedPilot}
          bind:this={createdPilotCloseButton}
        >
          Jag har skickat koden — stäng
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- ============================================================
     Modal: Inaktivera pilotkonto (enkel bekräftelse, ingen MFA)
     ============================================================ -->
{#if pendingDeactivatePilot}
  <div
    class="modal-backdrop"
    role="presentation"
    onclick={cancelDeactivatePilot}
    onkeydown={(e) => {
      if (e.key === 'Escape') cancelDeactivatePilot();
    }}
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deactivate-pilot-title"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
    >
      <h3 id="deactivate-pilot-title">Inaktivera pilotkonto</h3>
      <p>
        Pilotkontot <strong>{pendingDeactivatePilot.pilotId}</strong> inaktiveras och kan inte längre
        logga in. Audit-historiken bevaras spårbart i plattformen — ingenting raderas i tysthet. Detta
        går inte att ångra utan att skapa ett nytt pilotkonto med ny engångskod.
      </p>

      {#if deactivatePilotError}
        <div class="alert error" role="alert">{deactivatePilotError}</div>
      {/if}

      <div class="modal-actions">
        <button
          type="button"
          class="btn secondary"
          onclick={cancelDeactivatePilot}
          disabled={isDeactivatingPilot}
        >
          Avbryt
        </button>
        <button
          type="button"
          class="btn danger"
          onclick={() => void confirmDeactivatePilot()}
          disabled={isDeactivatingPilot}
        >
          {isDeactivatingPilot ? 'Inaktiverar…' : 'Inaktivera'}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- ============================================================
     Modal: Redigera kontaktuppgifter (S-04-31-followup)
     Pre-fyller existing values via GET /api/admin/users/{userId}/contacts.
     404 (inga existing contacts) → tomt formulär utan error-toast.
     Backend audit loggar bara fält-NAMN, inte värden.
     ============================================================ -->
{#if pendingEditPilot}
  <div
    class="modal-backdrop"
    role="presentation"
    onclick={cancelEditPilot}
    onkeydown={(e) => {
      if (e.key === 'Escape') cancelEditPilot();
    }}
  >
    <div
      class="modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-pilot-title"
      tabindex="-1"
      onclick={(e) => e.stopPropagation()}
      onkeydown={(e) => e.stopPropagation()}
    >
      <h3 id="edit-pilot-title">Redigera kontaktuppgifter</h3>
      <dl class="details">
        <dt>Konto-id</dt>
        <dd>
          <code>{pendingEditPilot.pilotId}</code>
        </dd>
      </dl>

      {#if isLoadingEditContacts}
        <p class="dim">Hämtar kontaktuppgifter…</p>
      {:else}
        <form
          class="form contact-form"
          onsubmit={(e) => {
            e.preventDefault();
            void confirmEditPilot();
          }}
        >
          <div class="field">
            <label for="edit-contact-name">Namn (obligatoriskt)</label>
            <input
              id="edit-contact-name"
              type="text"
              bind:value={editContactName}
              bind:this={editContactNameInput}
              maxlength="200"
              required
              autocomplete="off"
              placeholder="t.ex. Anna Andersson"
              disabled={isSavingEditContacts}
            />
          </div>
          <div class="field">
            <label for="edit-contact-phone">Mobilnummer (valfritt)</label>
            <input
              id="edit-contact-phone"
              type="text"
              bind:value={editContactPhone}
              maxlength="20"
              autocomplete="off"
              placeholder="+46701234567"
              disabled={isSavingEditContacts}
            />
            <p class="field-hint">Format: +landskod + nummer (t.ex. +46701234567).</p>
          </div>
          <div class="field">
            <label for="edit-contact-email">E-post (valfritt)</label>
            <input
              id="edit-contact-email"
              type="email"
              bind:value={editContactEmail}
              maxlength="255"
              autocomplete="off"
              placeholder="anna@example.se"
              disabled={isSavingEditContacts}
            />
          </div>
          <p class="contact-disclosure">
            Compuna AB lagrar dessa uppgifter för pilot-administration. Du kan begära radering via
            <a href="mailto:jonas@compuna.se">jonas@compuna.se</a>
            eller genom att avsluta deltagandet i appen.
          </p>

          {#if editError}
            <div class="alert error" role="alert">{editError}</div>
          {/if}

          <div class="modal-actions">
            <button
              type="button"
              class="btn secondary"
              onclick={cancelEditPilot}
              disabled={isSavingEditContacts}
            >
              Avbryt
            </button>
            <button type="submit" class="btn primary" disabled={isSavingEditContacts}>
              {isSavingEditContacts ? 'Sparar…' : 'Spara'}
            </button>
          </div>
        </form>
      {/if}
    </div>
  </div>
{/if}

<style>
  .users-section {
    display: grid;
    gap: 1rem;
  }

  .toolbar {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
  }
  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
    cursor: pointer;
    width: 2.4rem;
    height: 2.4rem;
    padding: 0.45rem;
    font-family: inherit;
    transition:
      color 120ms ease,
      border-color 120ms ease,
      background 120ms ease;
  }
  .icon-btn svg {
    width: 100%;
    height: 100%;
    display: block;
  }
  .icon-btn:hover:not(:disabled) {
    color: var(--text);
    border-color: var(--text-dim);
  }
  .icon-btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .icon-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .icon-btn-secondary {
    color: var(--accent);
    border-color: var(--accent);
  }
  .icon-btn-secondary:hover:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }
  .spinning {
    animation: icon-spin 900ms linear infinite;
  }
  @keyframes icon-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 0.55rem 1.1rem;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition:
      background-color 120ms ease,
      border-color 120ms ease,
      color 120ms ease;
  }
  .btn.small {
    font-size: 0.82rem;
    padding: 0.35rem 0.75rem;
  }
  .btn.primary {
    background: var(--accent);
    color: var(--bg);
    border-color: var(--accent);
  }
  .btn.primary:hover:not(:disabled),
  .btn.primary:focus-visible:not(:disabled) {
    background: var(--glow);
    border-color: var(--glow);
  }
  .btn.secondary {
    background: transparent;
    color: var(--accent);
    border-color: var(--accent);
  }
  .btn.secondary:hover:not(:disabled),
  .btn.secondary:focus-visible:not(:disabled) {
    background: var(--accent-soft);
    color: var(--glow);
  }
  .btn.danger {
    background: transparent;
    color: var(--danger);
    border-color: var(--danger);
  }
  .btn.danger:hover:not(:disabled),
  .btn.danger:focus-visible:not(:disabled) {
    background: var(--danger-soft);
  }
  .btn:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .alert {
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin-bottom: 1.25rem;
    font-size: 0.92rem;
    border: 1px solid var(--rule);
  }
  .alert.error {
    color: var(--danger);
    background: var(--danger-soft);
    border-color: var(--danger);
  }
  .alert.info {
    color: var(--text);
    background: var(--accent-soft);
    border-color: var(--accent);
  }
  .alert.mfa-gate {
    color: var(--text);
    background: var(--accent-soft);
    border-color: var(--accent);
  }
  .link-button {
    background: none;
    border: none;
    color: var(--glow);
    font: inherit;
    font-weight: 600;
    padding: 0;
    margin-left: 0.25rem;
    cursor: pointer;
    text-decoration: underline;
  }
  .link-button:hover,
  .link-button:focus-visible {
    color: var(--accent);
  }
  .link-button:focus-visible {
    outline: 2px solid var(--glow);
    outline-offset: 2px;
    border-radius: 2px;
  }

  .empty {
    padding: 1.5rem;
    border: 1px dashed var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
  }
  .kicker {
    font-size: 1.1rem;
    color: var(--text);
    font-weight: 500;
    border-left: 2px solid var(--accent);
    padding-left: 1rem;
    margin: 0 0 0.75rem;
  }

  .table-wrap {
    overflow-x: auto;
    border: 1px solid var(--rule);
    border-radius: 4px;
  }

  .users-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.92rem;
  }
  .users-table thead {
    background: rgba(212, 160, 86, 0.04);
  }
  .users-table th {
    text-align: left;
    padding: 0.7rem 0.85rem;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--text-dim);
    font-weight: 600;
    border-bottom: 1px solid var(--rule);
  }
  .users-table td {
    padding: 0.85rem;
    border-bottom: 1px solid var(--rule);
    vertical-align: middle;
  }
  .users-table tr:last-child td {
    border-bottom: none;
  }
  .users-table tr.inactive td {
    color: var(--text-dim);
  }
  .users-table td.dim {
    color: var(--text-dim);
    font-variant-numeric: tabular-nums;
    font-size: 0.85rem;
  }
  .actions-col {
    width: 1%;
    white-space: nowrap;
  }
  .actions-cell {
    display: flex;
    gap: 0.4rem;
    justify-content: flex-end;
  }

  .type-badge {
    display: inline-block;
    padding: 0.18rem 0.6rem;
    border-radius: 4px;
    font-size: 0.78rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 600;
    border: 1px solid var(--rule);
  }
  .type-badge.type-admin {
    background: var(--accent-soft);
    color: var(--glow);
    border-color: var(--accent);
  }
  .type-badge.type-pilot {
    background: transparent;
    color: var(--text-dim);
    border-color: var(--rule);
  }

  .identifier-cell {
    min-width: 0;
  }
  .identifier-row {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem 0.85rem;
    column-gap: 0.85rem;
  }
  .status-row {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.4rem;
  }
  .primary-id {
    color: var(--text);
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  .primary-id.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.88rem;
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 3px;
    padding: 0.18rem 0.45rem;
    font-weight: 500;
    color: var(--text);
    word-break: break-all;
  }
  .secondary-id {
    color: var(--text-dim);
    font-size: 0.85rem;
  }
  tr.inactive .primary-id {
    color: var(--text-dim);
  }
  .self-badge {
    display: inline-block;
    padding: 0.1rem 0.45rem;
    border-radius: 4px;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    background: var(--accent-soft);
    color: var(--glow);
    border: 1px solid var(--accent);
  }

  .status {
    display: inline-block;
    padding: 0.18rem 0.55rem;
    border-radius: 4px;
    font-size: 0.78rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 600;
  }
  .status-active {
    background: var(--accent-soft);
    color: var(--glow);
    border: 1px solid var(--accent);
  }
  .status-inactive {
    background: transparent;
    color: var(--text-dim);
    border: 1px solid var(--rule);
  }
  .status-pending {
    background: transparent;
    color: var(--text);
    border: 1px solid var(--text-dim);
  }

  .mfa-badge {
    display: inline-block;
    padding: 0.12rem 0.45rem;
    border-radius: 4px;
    font-size: 0.72rem;
    letter-spacing: 0.06em;
    font-weight: 600;
    border: 1px solid var(--rule);
  }
  .mfa-yes {
    color: var(--glow);
    border-color: var(--accent);
    background: var(--accent-soft);
  }
  .mfa-no {
    color: var(--danger);
    border-color: var(--danger);
    background: transparent;
  }

  /* ---- Modal ---- */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    display: grid;
    place-items: center;
    padding: 1rem;
    z-index: 100;
  }
  .modal {
    background: var(--bg-elev);
    border: 1px solid var(--accent);
    border-radius: 4px;
    padding: 1.5rem;
    max-width: 520px;
    width: 100%;
    color: var(--text);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  }
  .modal h3 {
    margin: 0 0 0.75rem;
    font-size: 1.2rem;
    letter-spacing: -0.01em;
  }
  .modal p {
    margin: 0 0 1rem;
    color: var(--text-dim);
  }
  .modal p strong {
    color: var(--text);
  }
  .modal .dim {
    color: var(--text-dim);
  }

  .form {
    display: grid;
    gap: 1rem;
    margin-bottom: 0.5rem;
  }
  .field {
    display: grid;
    gap: 0.4rem;
    border: none;
    padding: 0;
    margin: 0;
  }
  .field label {
    font-size: 0.85rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-dim);
    font-weight: 600;
  }
  .field-hint {
    margin: 0;
    color: var(--text-dim);
    font-size: 0.85rem;
    text-transform: none;
    letter-spacing: normal;
    font-weight: 400;
  }
  .field input[type='text'],
  .field input[type='email'] {
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text);
    padding: 0.6rem 0.8rem;
    font: inherit;
    font-size: 0.95rem;
    width: 100%;
    color-scheme: dark;
  }
  .field input[type='text']:focus,
  .field input[type='email']:focus {
    outline: 2px solid var(--accent);
    outline-offset: 0;
    border-color: var(--accent);
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    margin-top: 1rem;
  }

  .temp-password-box {
    background: var(--bg);
    border: 1px solid var(--accent);
    border-radius: 4px;
    padding: 1rem;
    margin: 0 0 1rem;
    display: grid;
    gap: 0.4rem;
  }
  .temp-password-label {
    font-size: 0.78rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-dim);
    font-weight: 600;
  }
  .temp-password {
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-size: 1rem;
    color: var(--glow);
    word-break: break-all;
    user-select: all;
  }

  .warning {
    background: var(--danger-soft);
    border: 1px solid var(--danger);
    color: var(--text);
    padding: 0.75rem 1rem;
    border-radius: 4px;
    margin: 0 0 1.25rem;
    font-size: 0.9rem;
    line-height: 1.5;
  }
  .warning strong {
    color: var(--danger);
  }
  .details {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.5rem 0.85rem;
    align-items: start;
    margin: 0 0 1rem;
  }
  .details dt {
    color: var(--text-dim);
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 600;
    padding-top: 0.4rem;
  }
  .details dd {
    margin: 0;
  }
  .details code {
    background: var(--bg);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.3rem 0.55rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.95rem;
    color: var(--text);
  }
  .activation-code {
    font-size: 1.15rem;
    letter-spacing: 0.18em;
    font-weight: 600;
    color: var(--glow) !important;
  }
  .code-row {
    display: flex;
    gap: 0.6rem;
    align-items: center;
    flex-wrap: wrap;
  }
  .copy-status {
    margin: 0 0 1rem;
    font-size: 0.85rem;
  }
  .copy-status.copied {
    color: var(--accent);
  }
  .copy-status.failed {
    color: var(--danger);
  }

  /* S-02-12 + S-04-31: kontakt-formulär i pilot-skapande-modal. */
  .contact-form {
    margin-top: 1rem;
  }
  .contact-disclosure {
    font-size: 0.78rem;
    color: var(--text-dim);
    line-height: 1.45;
    margin: 0.5rem 0 0;
  }
  .contact-disclosure a {
    color: var(--accent);
    text-decoration: underline;
  }
  .contact-disclosure a:hover,
  .contact-disclosure a:focus-visible {
    color: var(--glow);
  }

  @media (max-width: 720px) {
    .actions-cell {
      justify-content: flex-start;
    }
  }
</style>
